import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  getPayments,
  getPaymentById,
  createPayment,
  updatePayment,
  deletePayment,
} from "services";
import { handleAPIError, queryClient } from "utils";
import { IPayment } from "types";
import { toast } from "react-toastify";

// Fetch all payments
export const useGetPayments = () => {
  return useQuery({
    queryKey: [queryKeys.payments],
    queryFn: async () => await getPayments(),
  });
};

// Fetch a single payment by ID
export const useGetPaymentById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.payments, id], // Unique key for fetching a single payment
    queryFn: async () => await getPaymentById(id),
    enabled: !!id, // Only run query if ID is provided
  });
};

// Create a new payment
export const useCreatePayment = (): UseMutationResult<any, Error, IPayment> => {
  return useMutation({
    mutationFn: createPayment,
    onSuccess: async () => {
      toast.success("Payment method created successfully");

      // Invalidate payments query on success to refresh the list
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.payments],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Define the UpdatePaymentPayload type
export type UpdatePaymentPayload = {
  id: string; // Required payment ID
  data: IPayment;
};

// Hook to update a payment
export const useUpdatePayment = (): UseMutationResult<
  any,
  Error,
  UpdatePaymentPayload
> => {
  return useMutation<any, Error, UpdatePaymentPayload>({
    mutationFn: async ({ id, data }) => {
      return updatePayment(id, data); // Ensure this function matches your API
    },
    onSuccess: async () => {
      toast.success("Payment method updated successfully");
      // Invalidate payments query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.payments],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a payment
export const useDeletePayment = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      await deletePayment(id);
    },
    onSuccess: async () => {
      // Invalidate payments query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.payments],
      });

      toast.success("Payment method deleted.");
    },

    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
